import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useMediaQuery, useTheme } from '@mui/material';
import { useMutation } from '@tanstack/react-query';

import { filesAPIService } from '~/api/files';
import { vcardMicrositeAPIService } from '~/api/microsites/vcard-plus';
import { websiteAPIService } from '~/api/microsites/website';
import { qrcodeAPIService } from '~/api/qrcode';
import LoaderQrIcon from '~/assets/icons/svgs/loader-qr.svg';
import anonQRDefaultQr from '~/assets/images/anon-default-qr-code.webp';
import defaultVcardCoverImage from '~/assets/images/default-vcard-cover-image.webp';
import defaultVcardLogo from '~/assets/images/default-vcard-logo.webp';
import anonymQrDesktop from '~/assets/images/iphone-white-bg.webp';
// import { TextBox } from '~/components/atoms/text-box';
import { WebsiteMockup } from '~/components/atoms/mockup-previews/WebsiteMockup';
import { VCARD_PLUS_TEMPLATE_0 } from '~/components/organisms/previewBar/components/vcard-plus/components/VCARD_PLUS_TEMPLATE_0';
import { PreviewBarLayout } from '~/components/organisms/previewBar/PreviewBarLayout';
import Accordion from '~/components/ui/Accordion/Accordion';
import { Button } from '~/components/ui/button';
import { Icon } from '~/components/ui/icon';
import { StyledTab, StyledTabs } from '~/components/ui/StyledTabs';
import { Text } from '~/components/ui/text';
import { ANONYMOUS_QR_TEMPLATES_LIST, TEMPLATE_SEARCH_PARAM } from '~/constants/anonymousQR';
import { DEFAULT_QR_CODE_STYLES, DESKTOP_MIN_WIDTH, PREVIEW_BAR_VIEW_TYPE } from '~/constants/common';
import { QR_CODE_TYPE, QRCODE_DYNAMIC_MICROSITE_TYPE } from '~/constants/qr-code';
import { DEFAULT_CREATE_V_CARD_PLUS_PAYLOAD } from '~/constants/vCard';
import AnonymousDesignSection from '~/pages/HomePage/components/AnonymousDesignSection';
import AnonymousQRMobile from '~/pages/HomePage/components/AnonymousQRMobile/AnonymousQRMobile';
import AnonymousVCardPlusQR from '~/pages/HomePage/components/AnonymousVCardPlusQR';
import LastStepLogin from '~/pages/HomePage/components/LastStepLogin';
import { useQrCodeById, useVCardQrById, useWebsiteQrById } from '~/queries/qrCode';
import { getIsQrCodeStylesUpdating } from '~/store/reducers/anonQr/selectors';
import { COLORS } from '~/styles/colors';
import { DynamicQrCodeData } from '~/types/qrCode';
import { getFileFromLocalSource } from '~/utils/common';

import AnonymousWebsiteQR from '../AnonymousWebsiteQR';

import {
  StyledBlock,
  StyledContainer,
  StyledDesktopImageContainer,
  StyledDrawer,
  StyledFilterIcon,
  StyledImageButton,
  StyledIphoneContainer,
  StyledLastStepModalContent,
  StyledLeftBottomBlock,
  StyledLeftContainer,
  StyledLoaderQr,
  StyledLoginToCreateContainer,
  StyledMainContentWrapper,
  StyledModal,
  StyledModalBlurred,
  StyledQRImage,
  StyledQRType,
  StyledQRTypeButton,
  StyledQRTypesContainer,
  StyledWebsiteURL,
} from './styled';

const URL = 'https://qrbrain.io/';
const DESIGN_AVAILABLE_QRS = [QRCODE_DYNAMIC_MICROSITE_TYPE.WEBSITE, QRCODE_DYNAMIC_MICROSITE_TYPE.VCARD_PLUS];

export default function AnonymousQR() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();

  const [previewViewType, setPreviewViewType] = useState<string>(PREVIEW_BAR_VIEW_TYPE.QRCODE);

  // const isMobile = useMediaQuery(theme.breakpoints.down(TABLET_MIN_WIDTH));
  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_MIN_WIDTH));

  const selectedQrType = searchParams.get(TEMPLATE_SEARCH_PARAM);

  const selectedQrTemplate = useMemo(() => {
    if (selectedQrType) {
      return ANONYMOUS_QR_TEMPLATES_LIST.find(({ type }) => type === selectedQrType) || ANONYMOUS_QR_TEMPLATES_LIST[0];
    }

    return ANONYMOUS_QR_TEMPLATES_LIST[0];
  }, [selectedQrType]);

  const [isOpenedLastStep, setIsOpenedLastStep] = useState(false);

  const {
    mutate: createQrWebsite,
    data: websiteQrData,
    isPending: isPendingCreateQrWebsite,
  } = useMutation({
    mutationFn: websiteAPIService.createWebsiteSite,
  });

  const {
    mutate: createQrVCardPlus,
    data: vCardData,
    isPending: isPendingCreateQrVCardPlus,
  } = useMutation({
    mutationFn: vcardMicrositeAPIService.createVCardSite,
  });

  const { mutateAsync: uploadVCardLogo, data: vCardLogoId } = useMutation({
    mutationFn: filesAPIService.uploadImage,
  });

  const {
    mutate: createQrCodeMutate,
    data: createdQrCode,
    isPending: isPendingCreateQrCode,
  } = useMutation({
    mutationFn: qrcodeAPIService.createQrCode,
    onSuccess: async (response) => {
      if (selectedQrType === QRCODE_DYNAMIC_MICROSITE_TYPE.WEBSITE || selectedQrType === null) {
        createQrWebsite({
          url: 'https://example.com',
          qr_code_id: response.data.id,
        });
      } else if (selectedQrType === QRCODE_DYNAMIC_MICROSITE_TYPE.VCARD_PLUS) {
        const coverImage = await getFileFromLocalSource(`${window.location.origin}${defaultVcardCoverImage}`);
        const logoImage = await getFileFromLocalSource(`${window.location.origin}${defaultVcardLogo}`);
        await uploadVCardLogo(logoImage);
        uploadVCardCoverImage(coverImage);
      }
    },
  });

  const { mutate: uploadVCardCoverImage } = useMutation({
    mutationFn: filesAPIService.uploadImage,
    onSuccess: (res) => {
      createQrVCardPlus({
        ...DEFAULT_CREATE_V_CARD_PLUS_PAYLOAD,
        qr_code_id: createdQrCode?.data?.id,
        person: {
          ...DEFAULT_CREATE_V_CARD_PLUS_PAYLOAD.person,
          photo: {
            ...DEFAULT_CREATE_V_CARD_PLUS_PAYLOAD.person.photo,
            logo_id: vCardLogoId?.data,
          },
        },
        cover_image_id: res.data,
      });
    },
  });

  const { data: qrCodeData, isLoading: isQrLoading } = useQrCodeById<DynamicQrCodeData>({
    qrCodeId: createdQrCode?.data?.id,
    select: (res) => res.data,
  });

  const { data: websiteURL, isLoading: isLoadingWebsiteURL } = useWebsiteQrById({
    websiteQrId: websiteQrData?.data?.id,
    select: (res) => res.data.url,
    enabled: !selectedQrType || selectedQrType === QRCODE_DYNAMIC_MICROSITE_TYPE.WEBSITE,
  });

  const isAvailableQr =
    !selectedQrType ||
    selectedQrType === QRCODE_DYNAMIC_MICROSITE_TYPE.WEBSITE ||
    selectedQrType === QRCODE_DYNAMIC_MICROSITE_TYPE.VCARD_PLUS;

  useEffect(() => {
    if (isAvailableQr) {
      createQrCodeMutate({
        type: QR_CODE_TYPE.DYNAMIC,
        micrositeType: selectedQrType || QRCODE_DYNAMIC_MICROSITE_TYPE.WEBSITE,
        style: DEFAULT_QR_CODE_STYLES,
      });
    }
  }, [selectedQrType]);

  const handleClickQRType = (type) => {
    if (type.type !== 'more') {
      searchParams.set(TEMPLATE_SEARCH_PARAM, type.type);
      setSearchParams(searchParams);
    }
  };

  const renderSelectedTypeContent = () => {
    if (selectedQrTemplate.type === QRCODE_DYNAMIC_MICROSITE_TYPE.WEBSITE) {
      return <AnonymousWebsiteQR websiteQrId={websiteQrData?.data?.id} qrCodeId={createdQrCode?.data?.id} />;
    }

    if (selectedQrTemplate.type === QRCODE_DYNAMIC_MICROSITE_TYPE.VCARD_PLUS) {
      return <AnonymousVCardPlusQR qrCodeId={createdQrCode?.data?.id} vCardId={vCardData?.data?.id} />;
    }

    return (
      <StyledLoginToCreateContainer>
        <Button onClick={() => setIsOpenedLastStep(true)}>{t('buttons:loginCreate')}</Button>
      </StyledLoginToCreateContainer>
    );
  };

  const isQrExist = !!createdQrCode?.data?.id;

  return (
    <>
      <StyledContainer>
        {/*{!isMobile && (*/}
        {/*  <StyledTitleWrapper>*/}
        {/*    <Text variant="h1">{t('landing:createQr.titleLeft')}</Text>*/}
        {/*    <TextBox fontWeight={700} fontSize="46px" text={t('landing:createQr.titleRight')} />*/}
        {/*  </StyledTitleWrapper>*/}
        {/*)}*/}
        <StyledMainContentWrapper>
          {isDesktop ? (
            <>
              <StyledLeftContainer>
                <StyledBlock>
                  <Text variant="h2">1. {t('landing:possibilities.chooseType')}</Text>
                  <StyledQRTypesContainer>
                    {ANONYMOUS_QR_TEMPLATES_LIST.map((type) => {
                      const isSelected = type.title === selectedQrTemplate.title;
                      const isMore = type.type === 'more';
                      let textColor: string = COLORS.grey400;

                      if (isSelected) {
                        textColor = COLORS.primary;
                      } else if (isMore) {
                        textColor = COLORS.textMain;
                      }

                      return (
                        <StyledQRType key={type.title} $isMore={isMore}>
                          <StyledQRTypeButton
                            disabled={type.disabled}
                            $isSelected={isSelected}
                            $isMore={isMore}
                            aria-label={t(type.title)}
                            onClick={() => handleClickQRType(type)}
                          >
                            <Icon icon={type.icon} withParentColor={false} size={32} width={30} height={30} />
                          </StyledQRTypeButton>
                          <Text variant="buttonSmallMedium" sx={{ color: textColor, textAlign: 'center' }}>
                            {t(type.title)}
                          </Text>
                        </StyledQRType>
                      );
                    })}
                  </StyledQRTypesContainer>
                </StyledBlock>
                <StyledLeftBottomBlock>
                  <Accordion
                    defaultExpanded
                    summary={<Text variant="h2">2. {t('createQrStep1:p2')}</Text>}
                    details={renderSelectedTypeContent()}
                    sx={{ padding: 0, boxShadow: 'none', backgroundColor: COLORS.transparent, margin: 0 }}
                    summarySx={{
                      padding: '0 0 8px',
                      marginBottom: '24px',
                      borderBottom: `1px solid ${COLORS.grey150}`,
                      '.Mui-expanded': {
                        margin: '0 !important',
                      },
                    }}
                    detailsSx={{ padding: 0 }}
                  />
                  {DESIGN_AVAILABLE_QRS.includes(selectedQrTemplate.type) && (
                    <Accordion
                      defaultExpanded
                      summary={<Text variant="h2">3. {t('landing:anonymQr:designYourQr')}</Text>}
                      details={isDesktop && <AnonymousDesignSection qrCodeId={createdQrCode?.data?.id} />}
                      sx={{ padding: 0, boxShadow: 'none', backgroundColor: COLORS.transparent, margin: 0 }}
                      summarySx={{
                        padding: '0 0 8px',
                        marginBottom: '24px',
                        borderBottom: `1px solid ${COLORS.grey150}`,
                        '.Mui-expanded': {
                          margin: '0 !important',
                        },
                      }}
                      detailsSx={{ padding: 0 }}
                    />
                  )}
                </StyledLeftBottomBlock>
              </StyledLeftContainer>
              <StyledIphoneContainer>
                <Accordion
                  defaultExpanded
                  summary={
                    <Text variant="h2" sx={{ maxWidth: '100%', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                      4. {t('buttons:downloadQr')}
                    </Text>
                  }
                  details={
                    <>
                      <StyledTabs>
                        <StyledTab
                          onClick={() => {
                            setPreviewViewType(PREVIEW_BAR_VIEW_TYPE.PHONE);
                          }}
                          $isActive={previewViewType === PREVIEW_BAR_VIEW_TYPE.PHONE}
                        >
                          {t('buttons:preview')}
                        </StyledTab>
                        <StyledTab
                          disabled={false}
                          onClick={() => setPreviewViewType(PREVIEW_BAR_VIEW_TYPE.QRCODE)}
                          $isActive={previewViewType === PREVIEW_BAR_VIEW_TYPE.QRCODE}
                        >
                          {t('buttons:qrCode')}
                        </StyledTab>
                      </StyledTabs>
                      {previewViewType === PREVIEW_BAR_VIEW_TYPE.QRCODE ? (
                        <QrCodePreview
                          setIsOpenedLastStep={setIsOpenedLastStep}
                          isAvailableQr={isAvailableQr}
                          isPendingCreateQrCode={isPendingCreateQrCode}
                          qrCodeData={qrCodeData}
                          selectedQrType={selectedQrType}
                          websiteURL={websiteURL}
                          isLoadingWebsiteURL={isLoadingWebsiteURL}
                        />
                      ) : (
                        <PreviewBarLayout
                          withTabs={false}
                          isDisabledQrCodePreview={false}
                          isQrExist={isQrExist}
                          isQrLoading={
                            isQrLoading ||
                            isPendingCreateQrCode ||
                            isPendingCreateQrWebsite ||
                            isPendingCreateQrVCardPlus
                          }
                          qrCodeData={qrCodeData}
                          isAnonymous
                          sx={{ marginTop: '24px' }}
                        >
                          <SelectedTypePhonePreview
                            selectedQrTemplate={selectedQrTemplate}
                            isQrExist={isQrExist}
                            setIsOpenedLastStep={setIsOpenedLastStep}
                            websiteURL={websiteURL}
                            vCardId={vCardData?.data?.id}
                          />
                        </PreviewBarLayout>
                      )}
                    </>
                  }
                  sx={{ padding: 0, boxShadow: 'none', backgroundColor: COLORS.transparent, margin: 0 }}
                  summarySx={{
                    padding: '0 0 8px',
                    borderBottom: `1px solid ${COLORS.grey150}`,
                    '.Mui-expanded': {
                      margin: '0 !important',
                    },
                  }}
                  detailsSx={{ padding: 0 }}
                />
              </StyledIphoneContainer>
            </>
          ) : (
            <AnonymousQRMobile
              selectedQrTemplate={selectedQrTemplate}
              onClickQRType={handleClickQRType}
              setIsOpenedLastStep={setIsOpenedLastStep}
              isAvailableQr={isAvailableQr}
              websiteQrId={websiteQrData?.data?.id}
              vCardId={vCardData?.data?.id}
              isPendingCreateQrCode={isPendingCreateQrCode}
              qrCodeId={createdQrCode?.data?.id}
            />
          )}
        </StyledMainContentWrapper>
      </StyledContainer>
      {isOpenedLastStep && (
        <>
          {isDesktop ? (
            <StyledModal open={isOpenedLastStep} onClose={() => setIsOpenedLastStep(false)}>
              <div>
                <StyledModalBlurred onClick={() => setIsOpenedLastStep(false)} />
                <StyledLastStepModalContent>
                  <LastStepLogin qrCode={qrCodeData} selectedQR={selectedQrTemplate.id} />
                </StyledLastStepModalContent>
              </div>
            </StyledModal>
          ) : (
            <StyledDrawer anchor="bottom" open={isOpenedLastStep} onClose={() => setIsOpenedLastStep(false)}>
              <LastStepLogin qrCode={qrCodeData} selectedQR={selectedQrTemplate.id} />
            </StyledDrawer>
          )}
        </>
      )}
    </>
  );
}

type SelectedTypePhonePreviewProps = {
  isQrExist: boolean;
  selectedQrTemplate: (typeof ANONYMOUS_QR_TEMPLATES_LIST)[number];
  websiteURL?: string;
  setIsOpenedLastStep: Dispatch<SetStateAction<boolean>>;
  vCardId?: number;
};

function SelectedTypePhonePreview({
  isQrExist,
  selectedQrTemplate,
  websiteURL,
  setIsOpenedLastStep,
  vCardId,
}: SelectedTypePhonePreviewProps) {
  const { t } = useTranslation();

  const { data: vCardPlusData } = useVCardQrById({ vCardId });

  if (!isQrExist) return null;
  if (selectedQrTemplate.type === QRCODE_DYNAMIC_MICROSITE_TYPE.WEBSITE) {
    return <WebsiteMockup url={websiteURL} isAnonymous />;
  }

  if (selectedQrTemplate.type === QRCODE_DYNAMIC_MICROSITE_TYPE.VCARD_PLUS) {
    return <VCARD_PLUS_TEMPLATE_0 isAnonymous vCardQrData={vCardPlusData} />;
  }

  return (
    <StyledLoginToCreateContainer>
      <Button onClick={() => setIsOpenedLastStep(true)}>{t('buttons:loginCreate')}</Button>
    </StyledLoginToCreateContainer>
  );
}

type QrCodePreviewProps = {
  setIsOpenedLastStep: Dispatch<SetStateAction<boolean>>;
  isAvailableQr: boolean;
  selectedQrType: string;
  isPendingCreateQrCode: boolean;
  websiteURL?: string;
  qrCodeData?: DynamicQrCodeData;
  isLoadingWebsiteURL: boolean;
};

function QrCodePreview({
  isAvailableQr,
  setIsOpenedLastStep,
  selectedQrType,
  isPendingCreateQrCode,
  websiteURL,
  qrCodeData,
  isLoadingWebsiteURL,
}: QrCodePreviewProps) {
  const { t } = useTranslation();

  const isQrCodeStylesUpdating = useSelector(getIsQrCodeStylesUpdating);

  const [isLoaderIconLoaded, setIsLoaderIconLoaded] = useState(false);
  const [isQrImageLoaded, setIsQrImageLoaded] = useState(false);

  const qrImage = isAvailableQr ? qrCodeData?.qrImage : anonQRDefaultQr;
  const isDisabledDownloadQr = !isAvailableQr || isPendingCreateQrCode;
  const isWebsiteTemplate = !selectedQrType || selectedQrType === QRCODE_DYNAMIC_MICROSITE_TYPE.WEBSITE;
  let url = isWebsiteTemplate ? websiteURL || 'https://example.com' : URL;

  if (isLoadingWebsiteURL || (isPendingCreateQrCode && isWebsiteTemplate)) {
    url = 'https://example.com';
  }

  useEffect(() => {
    if (qrImage && isQrImageLoaded) {
      setIsQrImageLoaded(false);
    }
  }, [qrImage]);

  const handleClickDownloadQr = () => {
    if (isAvailableQr) {
      setIsOpenedLastStep(true);
    }
  };

  return (
    <StyledDesktopImageContainer>
      <StyledWebsiteURL>
        <StyledFilterIcon width={20} />
        <Text
          variant="button13Med"
          sx={{ fontWeight: 600, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {url}
        </Text>
      </StyledWebsiteURL>
      <img src={anonymQrDesktop} alt="anonym qr image" width="100%" />
      <img src={LoaderQrIcon} alt="" onLoad={() => setIsLoaderIconLoaded(true)} style={{ display: 'none' }} />
      <img src={qrImage} alt="" onLoad={() => setIsQrImageLoaded(true)} style={{ display: 'none' }} />
      {(isQrCodeStylesUpdating || isPendingCreateQrCode || !isQrImageLoaded || !qrImage) && isLoaderIconLoaded ? (
        <StyledLoaderQr src={LoaderQrIcon} alt="" />
      ) : (
        <StyledQRImage src={qrImage} alt="qr code" />
      )}

      <StyledImageButton disabled={isDisabledDownloadQr || !qrCodeData?.id} onClick={handleClickDownloadQr}>
        {t('buttons:downloadQr')}
      </StyledImageButton>
    </StyledDesktopImageContainer>
  );
}
